import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, MapPin, Trophy } from 'lucide-react';
import proTournamentInfoService, { ProTournamentInfo } from '../../services/proTournamentInfoService';
import api from '../../services/api';

const ACCENT = '#3fced1';

export const buildFileUrl = (path?: string | null): string | null => {
  if (typeof path !== 'string') return null;
  const trimmed = path.trim();
  if (!trimmed) return null; // leer/whitespace = kein Bild
  if (trimmed.startsWith('data:') || trimmed.startsWith('http')) return trimmed;
  return `${api.defaults.baseURL}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
};

/**
 * Sortierung: Das als Nächstes anstehende Turnier zuerst.
 * LIVE (läuft jetzt) → UPCOMING nach Startdatum aufsteigend →
 * ohne Datum → COMPLETED (neueste zuerst) ans Ende.
 */
/**
 * Sortier-Datum eines Turniers: normalerweise startDate. Bei bereits
 * GESTARTETEN Dachturnieren (PDC Pro Tour: startDate 9. Feb) sortiert sonst
 * das alte Startdatum sie VOR nähere Events — dann zählt stattdessen die
 * NÄCHSTE Schedule-Session (≥ heute, Tagesanfang).
 */
export const timelineSortDate = (
  t: ProTournamentInfo,
  now: number = Date.now()
): number | null => {
  const start = typeof t.startDate === 'number' ? t.startDate : null;
  if (start == null || start > now) return start;
  const day = new Date(now);
  const todayStart = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
  const upcoming = (t.schedule ?? [])
    .map((e) => (typeof e.date === 'number' ? e.date : null))
    .filter((d): d is number => d !== null && d >= todayStart);
  return upcoming.length > 0 ? Math.min(...upcoming) : start;
};

export const sortByTimeline = (tournaments: ProTournamentInfo[]): ProTournamentInfo[] => {
  const now = Date.now();
  const rank = (t: ProTournamentInfo): number => {
    if (t.status === 'LIVE') return 0;
    if (t.status === 'UPCOMING') return 1;
    if (t.status === 'COMPLETED') return 3;
    return 2;
  };
  return [...tournaments].sort((a, b) => {
    const rankDiff = rank(a) - rank(b);
    if (rankDiff !== 0) return rankDiff;
    if (a.status === 'COMPLETED') {
      const aStart = typeof a.startDate === 'number' ? a.startDate : null;
      const bStart = typeof b.startDate === 'number' ? b.startDate : null;
      return (bStart ?? 0) - (aStart ?? 0); // Beendete: neueste zuerst
    }
    const aDate = timelineSortDate(a, now);
    const bDate = timelineSortDate(b, now);
    if (aDate == null && bDate == null) return 0;
    if (aDate == null) return 1;
    if (bDate == null) return -1;
    return aDate - bDate; // Kommende: nächstes zuerst
  });
};

/** Status-Chip (eckig) für den Content-Bereich der Karte — nie auf dem Bild. */
export const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  if (status === 'LIVE') {
    return (
      <span className="inline-flex items-center gap-1.5 rounded bg-red-500/90 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
        Live
      </span>
    );
  }
  if (status === 'COMPLETED') {
    return (
      <span className="inline-flex items-center rounded bg-zinc-800 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-300">
        Beendet
      </span>
    );
  }
  return null;
};

export const TournamentCard: React.FC<{
  tournament: ProTournamentInfo;
  onClick: () => void;
  /** true = volle Breite (Grid auf /pdc-turniere), false = feste Slider-Breite */
  fluid?: boolean;
  /** Gesetzt → Karte ist ein echter Link (<a>), z.B. für interne SEO-Links (2026-09-03) */
  href?: string;
}> = ({ tournament, onClick, fluid = false, href }) => {
  const [imageError, setImageError] = useState(false);
  const thumbnail = buildFileUrl(tournament.thumbnail || tournament.heroImage);
  const showImage = thumbnail && !imageError;
  const orgTier = [tournament.organization, tournament.tier].filter(Boolean).join(' · ');

  const cardClassName = `group cursor-pointer overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 transition-all duration-300 hover:-translate-y-1 hover:border-[#3fced1]/50 hover:shadow-[0_16px_40px_-12px_rgba(63,206,209,0.3)] ${
    fluid ? 'w-full' : 'w-[300px] shrink-0 snap-start sm:w-[340px]'
  }`;
  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) =>
    href ? (
      <RouterLink
        to={href}
        aria-label={tournament.name || 'PDC-Turnier'}
        onClick={onClick}
        className={`block ${cardClassName}`}
      >
        {children}
      </RouterLink>
    ) : (
      <div
        role="button"
        tabIndex={0}
        aria-label={tournament.name || 'PDC-Turnier'}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className={cardClassName}
      >
        {children}
      </div>
    );

  return (
    <Wrapper>
      {/* Banner (16:9 – KI-generiert) */}
      <div className="relative aspect-video w-full overflow-hidden bg-zinc-800">
        {showImage ? (
          <img
            src={thumbnail as string}
            alt={tournament.name || ''}
            loading="lazy"
            onError={() => setImageError(true)}
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="relative flex h-full w-full items-center justify-center bg-gradient-to-br from-zinc-800 via-zinc-900 to-black">
            <div
              className="pointer-events-none absolute inset-0"
              style={{ background: 'radial-gradient(60% 80% at 30% 20%, rgba(63,206,209,0.12), transparent 65%)' }}
            />
            <Trophy size={36} className="text-zinc-600" />
          </div>
        )}
      </div>

      {/* Info: Status-Chip + Org/Tier über dem Titel */}
      <div className="p-4">
        {(tournament.status === 'LIVE' || tournament.status === 'COMPLETED' || orgTier) && (
          <div className="mb-2 flex items-center gap-1.5">
            <StatusBadge status={tournament.status} />
            {orgTier && (
              <span className="truncate text-[10.5px] text-zinc-500">{orgTier}</span>
            )}
          </div>
        )}
        <h3 className="line-clamp-1 text-base font-bold text-white transition-colors group-hover:text-[#3fced1]">
          {tournament.shortName || tournament.name}
        </h3>
        <div className="mt-2 space-y-1.5 text-sm text-gray-400">
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} className="shrink-0" style={{ color: ACCENT }} />
            <span className="line-clamp-1">{tournament.dateLabel || 'Termin folgt'}</span>
          </div>
          {(tournament.venue || tournament.city) && (
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="shrink-0" style={{ color: ACCENT }} />
              <span className="line-clamp-1">
                {[tournament.venue, tournament.city].filter(Boolean).join(', ')}
              </span>
            </div>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

/**
 * Startseiten-Slider: Professionelle Turniere in Timeline-Reihenfolge –
 * das als Nächstes anstehende Turnier steht vorne. Klick → /turniere/:slug.
 */
const ProTournamentsSection: React.FC = () => {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState<ProTournamentInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    proTournamentInfoService
      .list({ limit: 20 })
      .then((list) => {
        if (!cancelled) setTournaments(list.filter((t) => t && t.slug));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Startseite zeigt nur Laufendes & Kommendes — beendete Turniere gibt es
  // gesammelt unter /pdc-turniere (Bug 2026-07-12: World Cup hing wochenlang
  // als "kommend" im Slider, weil der Status nicht nachgezogen wurde).
  const sorted = useMemo(
    () => sortByTimeline(tournaments).filter((t) => t.status !== 'COMPLETED'),
    [tournaments]
  );

  const scrollBy = (direction: 1 | -1) => {
    scrollRef.current?.scrollBy({ left: direction * 360, behavior: 'smooth' });
  };

  if (!loading && sorted.length === 0) return null;

  return (
    <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h2 className="section-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
            PDC-Turniere
          </h2>
          <p className="mt-1 pl-[0.9rem] text-sm text-gray-400">
            Die großen Turniere der Darts-Welt – immer aktuell
          </p>
        </div>
        <div className="flex items-center gap-2">
          <RouterLink
            to="/pdc-turniere"
            className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 px-3.5 py-2 text-sm font-medium text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white"
          >
            Alle ansehen
            <ArrowRight size={14} style={{ color: ACCENT }} />
          </RouterLink>
          <div className="hidden gap-2 sm:flex">
          <button
            type="button"
            aria-label="Zurück scrollen"
            onClick={() => scrollBy(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            aria-label="Weiter scrollen"
            onClick={() => scrollBy(1)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-zinc-700 text-gray-300 transition-colors hover:border-[#3fced1]/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3fced1]/60"
          >
            <ChevronRight size={18} />
          </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex gap-4 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="w-[300px] shrink-0 animate-pulse overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 sm:w-[340px]"
            >
              <div className="aspect-video w-full bg-zinc-800" />
              <div className="space-y-2 p-4">
                <div className="h-5 w-3/4 rounded bg-zinc-800" />
                <div className="h-4 w-1/2 rounded bg-zinc-800" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {sorted.map((tournament) => (
            <TournamentCard
              key={tournament.slug || tournament.id}
              tournament={tournament}
              onClick={() => navigate(`/turniere/${tournament.slug}`)}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default ProTournamentsSection;
